import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Input, Radio, Result, Space, Spin, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { BUYER_ACCOUNT_API, SaleBuyerSession, SaleApiError, SalePublicInfo, SaleResource, newSaleRequestId, saleBrowserSecret, saleRequest } from '../../services/saleLinks';
import { detectSaleEmbeddedBrowser, saleError, useSaleText } from './saleUi';
import SaleResourceView from './SaleResourceView';
import SaleAccountLogin from './SaleAccountLogin';
import SaleBrowserNotice from './SaleBrowserNotice';
import './saleLinks.css';

const SaleBuyer: React.FC = () => {
  const { linkCode = '' } = useParams();
  const text = useSaleText();
  const embeddedBrowser = detectSaleEmbeddedBrowser();
  const [info, setInfo] = useState<SalePublicInfo | null>(null);
  const [resource, setResource] = useState<SaleResource | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [storageError, setStorageError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [session, setSession] = useState<SaleBuyerSession | null>(null);
  const [mode, setMode] = useState<'DEVICE' | 'ACCOUNT'>('DEVICE');
  const opening = useRef('');
  const generation = useRef(0);

  useEffect(() => {
    const immersiveHtml = resource?.category === 'html';
    document.documentElement.classList.toggle('sale-html-fullscreen-active', immersiveHtml);
    document.body.classList.toggle('sale-html-fullscreen-active', immersiveHtml);
    return () => {
      document.documentElement.classList.remove('sale-html-fullscreen-active');
      document.body.classList.remove('sale-html-fullscreen-active');
    };
  }, [resource?.category]);

  useEffect(() => {
    const htmlTitle = resource?.category === 'html' ? resource.htmlTitle?.trim() : '';
    document.title = htmlTitle || info?.title || text('资源激活 - 图床小镇', 'Activate resource - Imgbed');
    return () => { document.title = text('资源激活 - 图床小镇', 'Activate resource - Imgbed'); };
  }, [info?.title, resource?.category, resource?.htmlTitle, text]);

  useEffect(() => {
    if (embeddedBrowser) {
      setLoading(false);
      return;
    }
    const current = ++generation.current;
    setLoading(true); setError(''); setResource(null); setInfo(null); setStorageError(false);
    void (async () => {
      try {
        const metadata = await saleRequest<SalePublicInfo>(`/public/${linkCode}`);
        if (generation.current !== current) return;
        setInfo(metadata);
        let buyerSession: SaleBuyerSession | null = null;
        // Older server nodes do not expose account capability during a staged rollout.
        if (typeof metadata.accountEnabled === 'boolean') {
          buyerSession = await saleRequest<SaleBuyerSession>(`${BUYER_ACCOUNT_API}/session`);
          if (generation.current !== current) return;
          setSession(buyerSession);
        }
        let secret: string | null = null;
        try { secret = saleBrowserSecret(linkCode, false); } catch { setStorageError(true); }
        if (!secret && !buyerSession?.authenticated) return;
        if (!opening.current) opening.current = newSaleRequestId();
        try {
          const result = await saleRequest<SaleResource>(`/public/${linkCode}/access`, 'POST', { requestId: opening.current }, secret || undefined);
          if (generation.current === current) setResource(result);
        } catch (failure) {
          if (!(failure instanceof SaleApiError && failure.code === 'sale.activationRequired')) throw failure;
        }
      } catch (failure) {
        if (generation.current === current) setError(saleError(failure, text));
      } finally { if (generation.current === current) setLoading(false); }
    })();
    return () => { generation.current = current + 1; };
  }, [linkCode, retry, text, embeddedBrowser]);

  const activate = async ({ code }: { code: string }) => {
    if (busy) return;
    if (mode === 'ACCOUNT' && !session?.authenticated) return;
    let secret: string | undefined;
    try {
      if (mode === 'DEVICE') secret = saleBrowserSecret(linkCode, true)!;
      if (!opening.current) opening.current = newSaleRequestId();
    } catch { setStorageError(true); return; }
    const current = generation.current;
    setBusy(true); setError('');
    try {
      await saleRequest(`/public/${linkCode}/activate`, 'POST', { code, bindingMode: mode }, secret);
      const result = await saleRequest<SaleResource>(`/public/${linkCode}/access`, 'POST', { requestId: opening.current }, secret);
      if (generation.current === current) setResource(result);
    } catch (failure) {
      if (generation.current === current) {
        setError(saleError(failure, text));
        if (failure instanceof SaleApiError && failure.code === 'sale.account.loginRequired') {
          setSession((previous) => previous && { ...previous, authenticated: false, email: null, expiresAt: null });
        }
      }
    }
    finally { setBusy(false); }
  };

  if (embeddedBrowser) return <SaleBrowserNotice browser={embeddedBrowser} />;
  if (loading) return <div className="sale-loading"><Spin size="large" /></div>;
  if (!info) return <div className="sale-page"><Result status="warning" title={text('链接暂不可用', 'Link unavailable')}
    subTitle={error} extra={<Button onClick={() => setRetry((value) => value + 1)}>{text('重试', 'Retry')}</Button>} /></div>;
  if (resource?.category === 'html') {
    return <div className="sale-html-fullscreen">
      <SaleResourceView resource={resource} immersiveHtml />
    </div>;
  }

  return <div className={`sale-page sale-buyer ${resource ? 'sale-buyer-unlocked' : ''}`}>
    <Card>
      <Typography.Title level={2}>{info.title}</Typography.Title>
      {info.description && <Typography.Paragraph className="sale-multiline">{info.description}</Typography.Paragraph>}
      {info.contact && <Typography.Paragraph type="secondary">{text('联系卖家：', 'Contact seller: ')}{info.contact}</Typography.Paragraph>}
      {session && (session.accountEnabled || session.authenticated) && <div className="sale-account-entry">
        <Link to="/sale/account">{text('我的资源 / 买家账号', 'My resources / buyer account')}</Link>
      </div>}
      {error && <Alert type="error" showIcon message={error} action={<Button onClick={() => setRetry((value) => value + 1)}>{text('重试', 'Retry')}</Button>} />}
      {resource ? <SaleResourceView resource={resource} /> : <>
        {session && (session.accountEnabled || session.authenticated) && <>
          <Radio.Group value={mode} onChange={(event) => setMode(event.target.value)} disabled={busy} className="sale-binding-mode">
            <Radio.Button value="DEVICE">{text('绑定当前设备', 'Bind this device')}</Radio.Button>
            <Radio.Button value="ACCOUNT" disabled={!session.accountEnabled}>{text('绑定邮箱账号', 'Bind email account')}</Radio.Button>
          </Radio.Group>
          {mode === 'ACCOUNT' && <SaleAccountLogin session={session} disabled={busy} onChange={(next) => {
            setSession(next); opening.current = ''; setRetry((value) => value + 1);
          }} />}
        </>}
        {storageError && mode === 'DEVICE' && <Alert type="warning" showIcon message={text('当前浏览器无法保存授权，请开启网站存储或更换浏览器。激活码尚未消耗。', 'This browser cannot save authorization. Enable site storage or change browsers. No code has been consumed.')} />}
        {info.status === 'PAUSED' && <Alert type="info" message={text('卖家已暂停新激活。', 'New activations are paused.')} />}
        <Form layout="vertical" onFinish={activate} className="sale-activation-form">
          <Form.Item label={text('激活码', 'Activation code')} name="code" rules={[{ required: true, whitespace: true, message: text('请输入激活码', 'Enter your activation code') }]}>
            <Input size="large" maxLength={100} autoComplete="off" spellCheck={false} placeholder="XXXXX-XXXXX-XXXXX-XXXXX" />
          </Form.Item>
          <Button type="primary" size="large" block htmlType="submit" loading={busy} disabled={(mode === 'DEVICE' ? storageError : !session?.authenticated || !session.accountEnabled) || info.status === 'PAUSED'}>
            {text('激活并打开', 'Activate and open')}
          </Button>
        </Form>
        <Space direction="vertical">
          <Typography.Text type="secondary">{mode === 'ACCOUNT'
            ? text('激活后绑定当前邮箱，可在其他设备登录同一账号使用；绑定方式不可更改。', 'Activation binds to this email. Sign in with it on other devices; the binding cannot be changed.')
            : text('一个码只能绑定一个浏览器，激活后可重复访问。', 'Each code binds to one browser and allows repeat access.')}</Typography.Text>
          {mode === 'DEVICE' && <Typography.Text type="secondary">{text('请避免无痕模式。更换浏览器或清除网站数据后，请联系卖家补发。', 'Avoid private browsing. Contact the seller after changing browsers or clearing site data.')}</Typography.Text>}
        </Space>
      </>}
    </Card>
  </div>;
};
export default SaleBuyer;
